import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { Button, Card, Form, Modal } from "react-bootstrap";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { companyID, getEmployeeLocalDetails } from "../../../../auth";
import Breadcrumbs from "../../../../components/Breadcrumbs";
import Loading from "../../../../components/Loading";
import {
  fetchSupportTicketDetail,
  fetchTicketChatMessages,
  updateSupportTicketStatus,
} from "../../../../services/supportService";
import { decrypt } from "../../../../utils/crypto";
import SupportTicketStatusBadge from "./components/SupportTicketStatusBadge";
import useSupportWebSocket from "../../../../hooks/useSupportWebSocket";
import { javaApi } from "../../../../api/axios";
import config from "../../../../config";
import utc from "dayjs/plugin/utc";
import dayjs from "dayjs";

dayjs.extend(utc);

const SupportTicketDetail = () => {
  const { ticketId } = useParams();
  const decryptedId = useMemo(() => {
    try {
      return decrypt(ticketId);
    } catch (error) {
      return ticketId;
    }
  }, [ticketId]);
  const { t } = useTranslation();
  const companyId = companyID();
  const employeeDetails = getEmployeeLocalDetails();

  // Use employee ID for WebSocket connection instead of company ID
  const userId =
    employeeDetails?.employee_id || employeeDetails?.id || companyId;

  const [ticket, setTicket] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [showMediaPreview, setShowMediaPreview] = useState(false);
  const [previewMediaUrl, setPreviewMediaUrl] = useState("");
  const [previewMediaType, setPreviewMediaType] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [mediaCaption, setMediaCaption] = useState("");
  const [showImagePreview, setShowImagePreview] = useState(false);
  const [imagePreviewPosition, setImagePreviewPosition] = useState({
    x: 0,
    y: 0,
  });
  const [uploadedMediaData, setUploadedMediaData] = useState(null);
  const [isMediaUploaded, setIsMediaUploaded] = useState(false);
  const [closingTicket, setClosingTicket] = useState(false);
  const typingTimeoutRef = useRef(null);
  const chatContainerRef = useRef(null);
  const fileInputRef = useRef(null);
  const optimisticMessageTimeoutRef = useRef(new Map());

  // WebSocket callbacks
  const handleMessageReceived = useCallback((message) => {
    // Remove any optimistic message with the same content to avoid duplicates
    setChatMessages((prev) => {
      const optimisticMessages = prev.filter((m) => m.isOptimistic);

      const filtered = prev.filter((msg) => {
        // Match optimistic messages more broadly
        if (msg.isOptimistic) {
          // For media messages, match by attachment URL and sender
          if (message.attachmentUrl && msg.attachmentUrl) {
            const urlMatch = msg.attachmentUrl === message.attachmentUrl;
            const senderMatch =
              msg.senderType === message.senderType &&
              msg.senderId == message.senderId;

            if (urlMatch && senderMatch) {
              // Clear the timeout for this optimistic message
              const timeoutId = optimisticMessageTimeoutRef.current.get(msg.id);
              if (timeoutId) {
                clearTimeout(timeoutId);
                optimisticMessageTimeoutRef.current.delete(msg.id);
              }
              return false;
            }
          }
          // For text messages or mixed messages, match by content and sender
          else if (
            (!message.attachmentUrl && !msg.attachmentUrl) ||
            (message.attachmentUrl && msg.attachmentUrl)
          ) {
            const messageMatch = msg.message.trim() === message.message.trim();
            const senderMatch =
              msg.senderType === message.senderType &&
              msg.senderId == message.senderId;

            if (messageMatch && senderMatch) {
              // Clear the timeout for this optimistic message
              const timeoutId = optimisticMessageTimeoutRef.current.get(msg.id);
              if (timeoutId) {
                clearTimeout(timeoutId);
                optimisticMessageTimeoutRef.current.delete(msg.id);
              }
              return false;
            }
          }
        }
        return true;
      });

      const exists = filtered.some((msg) => {
        if (msg.id === message.id) return true;

        if (message.attachmentUrl && msg.attachmentUrl) {
          return (
            msg.attachmentUrl === message.attachmentUrl &&
            msg.senderType === message.senderType &&
            Math.abs(
              new Date(msg.createdAt || msg.timestamp) -
                new Date(message.createdAt || message.timestamp),
            ) < 10000
          );
        }

        return (
          !message.attachmentUrl &&
          !msg.attachmentUrl &&
          msg.message.trim() === message.message.trim() &&
          msg.senderType === message.senderType &&
          Math.abs(
            new Date(msg.createdAt || msg.timestamp) -
              new Date(message.createdAt || message.timestamp),
          ) < 10000
        );
      });

      if (!exists) {
        return [...filtered, message];
      } else {
        return filtered;
      }
    });

    // Scroll to bottom
    setTimeout(() => {
      if (chatContainerRef.current) {
        chatContainerRef.current.scrollTop =
          chatContainerRef.current.scrollHeight;
      }
    }, 100);
  }, []);

  const handleTypingReceived = useCallback((data) => {
    setIsTyping(data.isTyping);

    if (data.isTyping) {
      // Auto-clear typing indicator after 3 seconds
      setTimeout(() => setIsTyping(false), 3000);
    }
  }, []);

  // Initialize WebSocket
  const {
    sendMessage: sendWebSocketMessage,
    sendTypingIndicator,
    isConnected,
  } = useSupportWebSocket({
    threadUuid: ticket?.threadUuid,
    userId: userId,
    onMessageReceived: handleMessageReceived,
    onTypingReceived: handleTypingReceived,
  });

  // Add debug logging for WebSocket initialization
  useEffect(() => {}, [
    ticket?.threadUuid,
    userId,
    companyId,
    employeeDetails,
    isConnected,
  ]);

  // Add effect to monitor chat messages changes
  useEffect(() => {}, [chatMessages]);

  // Scroll to bottom when typing indicator appears
  useEffect(() => {
    if (isTyping) {
      setTimeout(() => {
        if (chatContainerRef.current) {
          chatContainerRef.current.scrollTop =
            chatContainerRef.current.scrollHeight;
        }
      }, 100);
    }
  }, [isTyping]);

  const loadTicket = useCallback(async () => {
    if (!decryptedId) return;
    setLoading(true);
    try {
      const response = await fetchSupportTicketDetail(decryptedId);
      const details =
        response?.result || response?.data || response?.ticket || response;
      setTicket(details);

      // Load chat messages using threadUuid
      if (details?.threadUuid) {
        const chatResponse = await fetchTicketChatMessages(details.threadUuid);
        let messages = Array.isArray(chatResponse)
          ? chatResponse
          : chatResponse?.result ||
            chatResponse?.data ||
            chatResponse?.messages ||
            [];

        // Process loaded messages to ensure they have correct properties for rendering
        messages = messages.map((msg) => {
          // Ensure message has proper messageType and hasCaption properties
          let processedMsg = { ...msg };

          if (msg.attachmentUrl) {
            // Determine if it's video or image
            const isVideo = /\.(mp4|webm|ogg|mov|avi)$/i.test(
              msg.attachmentUrl,
            );

            // Check if message has actual text content (not just default attachment text)
            const hasActualMessage =
              msg.message &&
              msg.message.trim() !== "" &&
              msg.message !== "📎 Attachment" &&
              msg.message !== "📷 Image" &&
              msg.message !== "🎥 Video";

            // Set messageType based on content
            if (hasActualMessage) {
              processedMsg.messageType = "MIXED"; // Both media and text
              processedMsg.hasCaption = true;
            } else {
              processedMsg.messageType = isVideo ? "VIDEO" : "IMAGE"; // Media only
              processedMsg.hasCaption = false;
            }

            // Ensure message text is clean
            if (!hasActualMessage) {
              processedMsg.message = "";
            }
          } else {
            processedMsg.messageType = "TEXT";
            processedMsg.hasCaption = false;
          }

          return processedMsg;
        });

        setChatMessages(messages);
      }
    } catch (error) {
      console.error("Error fetching ticket detail", error);
      toast.error(t("Unable to fetch ticket details"));
    } finally {
      setLoading(false);
    }
  }, [decryptedId, t]);

  useEffect(() => {
    loadTicket();

    // Copy ref to variable to avoid stale closure warning
    const timeoutMap = optimisticMessageTimeoutRef.current;

    // Handle WebSocket authentication errors
    const handleAuthError = (event) => {
      console.error("WebSocket authentication error:", event.detail);
      toast.error(
        t("Authentication error. Please refresh the page and try again."),
      );
    };

    window.addEventListener("websocket-auth-error", handleAuthError);

    // Cleanup typing timeout on unmount
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      // Use the copied variable
      timeoutMap.forEach((timeoutId) => {
        clearTimeout(timeoutId);
      });
      timeoutMap.clear();

      window.removeEventListener("websocket-auth-error", handleAuthError);
    };
  }, [loadTicket, t]);

  // Close ticket function - only available for admin/support users
  const handleCloseTicket = async () => {
    if (companyId !== 1) {
      toast.error(t("Only support team can close tickets"));
      return;
    }

    try {
      setClosingTicket(true);
      const response = await updateSupportTicketStatus(ticket.id, "CLOSED");
      
      // Update local ticket state
      setTicket(prev => ({ ...prev, status: "CLOSED" }));
      
      toast.success(response?.message || t("Ticket closed successfully"));
    } catch (error) {
      console.error("Error closing ticket:", error);
      toast.error(error?.response?.data?.message || t("Failed to close ticket"));
    } finally {
      setClosingTicket(false);
    }
  };

  const handleSendMessage = async () => {
    // Prevent sending messages if ticket is closed
    if (ticket?.status === "CLOSED") {
      toast.error(t("Cannot send messages to a closed ticket"));
      return;
    }

    if (!newMessage.trim() || sending || !ticket?.threadUuid || !isConnected) {
      if (!isConnected) {
        toast.error(
          t(
            "WebSocket connection is not established. Please wait and try again.",
          ),
        );
      }
      return;
    }

    setSending(true);
    const messageToSend = newMessage.trim();

    // Determine sender type based on company ID
    const senderType = companyId !== 1 ? "CLIENT" : "SUPPORT";

    // Create optimistic message for immediate UI update
    const optimisticMessage = {
      id: `optimistic_${Date.now()}`,
      threadUuid: ticket.threadUuid,
      senderId: parseInt(userId, 10),
      senderType: senderType,
      message: messageToSend,
      messageType: "TEXT",
      attachmentUrl: null,
      createdAt: new Date().toISOString(),
      senderDetails: null,
      isOptimistic: true,
      timestamp: Date.now(),
    };

    try {
      // Immediately add message to chat for better UX
      setChatMessages((prev) => [...prev, optimisticMessage]);

      // Set timeout to convert optimistic message to real message if no WebSocket response after 5 seconds
      const timeoutId = setTimeout(() => {
        setChatMessages((prev) => {
          return prev.map((msg) => {
            if (msg.id === optimisticMessage.id && msg.isOptimistic) {
              console.warn(
                "Converting optimistic message to real message due to timeout:",
                msg,
              );
              // Convert optimistic message to real message
              return {
                ...msg,
                id: `real_${Date.now()}`,
                isOptimistic: false,
              };
            }
            return msg;
          });
        });
        optimisticMessageTimeoutRef.current.delete(optimisticMessage.id);
      }, 5000); // Reduced from 10 to 5 seconds

      optimisticMessageTimeoutRef.current.set(optimisticMessage.id, timeoutId);

      // Clear input immediately
      setNewMessage("");

      // Scroll to bottom immediately
      setTimeout(() => {
        if (chatContainerRef.current) {
          chatContainerRef.current.scrollTop =
            chatContainerRef.current.scrollHeight;
        }
      }, 50);

      // Send via WebSocket with messageType
      await sendWebSocketMessage(messageToSend, senderType, null, "TEXT");

      // Stop typing indicator
      sendTypingIndicator(false, companyId !== 1 ? "Client" : "Support Agent");

      // Clear typing timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = null;
      }
    } catch (error) {
      console.error("Error sending message:", error);

      // Remove optimistic message on error
      setChatMessages((prev) => {
        const timeoutId = optimisticMessageTimeoutRef.current.get(
          optimisticMessage.id,
        );
        if (timeoutId) {
          clearTimeout(timeoutId);
          optimisticMessageTimeoutRef.current.delete(optimisticMessage.id);
        }
        return prev.filter((msg) => msg.id !== optimisticMessage.id);
      });

      // Restore message to input
      setNewMessage(messageToSend);

      if (error.message.includes("Sender not found")) {
        toast.error(
          t(
            "User authentication error. Please refresh the page and try again.",
          ),
        );
      } else {
        toast.error(t("Failed to send message: ") + error.message);
      }
    } finally {
      setSending(false);
    }
  };

  const handleTyping = useCallback(
    (value) => {
      setNewMessage(value);

      // Clear previous timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }

      // Determine user name based on company ID
      const userName = companyId !== 1 ? "Client" : "Support Agent";

      // Send typing indicator only if WebSocket is connected
      if (isConnected) {
        if (value.trim()) {
          sendTypingIndicator(true, userName);

          // Stop typing indicator after 2 seconds of inactivity
          typingTimeoutRef.current = setTimeout(() => {
            sendTypingIndicator(false, userName);
          }, 2000);
        } else {
          sendTypingIndicator(false, userName);
        }
      }
    },
    [sendTypingIndicator, isConnected, companyId],
  );

  const uploadMedia = async (file) => {
    if (!file) return null;

    // Validate file type
    if (!file.type.startsWith("image/") && !file.type.startsWith("video/")) {
      toast.error(t("Please select a valid image or video file"));
      return null;
    }

    // Validate file size (max 50MB for videos, 10MB for images)
    const maxSize = file.type.startsWith("video/")
      ? 50 * 1024 * 1024
      : 10 * 1024 * 1024;
    const fileTypeText = file.type.startsWith("video/") ? "Video" : "Image";
    const maxSizeText = file.type.startsWith("video/") ? "50MB" : "10MB";

    if (file.size > maxSize) {
      toast.error(t(`${fileTypeText} size should not exceed ${maxSizeText}`));
      return null;
    }

    setUploadingMedia(true);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await javaApi.post("/api/v1/upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data && response.data.url) {
        const fullMediaUrl = response.data.url;
        return {
          filename: response.data.filename,
          url: response.data.url,
          fullUrl: fullMediaUrl,
          fileType: file.type,
        };
      }

      throw new Error("Invalid response from upload API");
    } catch (error) {
      console.error("Error uploading media:", error);
      const mediaType = file.type.startsWith("video/") ? "video" : "image";
      toast.error(
        t(`Failed to upload ${mediaType}: `) +
          (error.response?.data?.message || error.message),
      );
      return null;
    } finally {
      setUploadingMedia(false);
    }
  };

  const deleteMedia = async (filename) => {
    if (!filename) return false;

    try {
      await javaApi.delete(`/api/v1/upload/${filename}`);
      return true;
    } catch (error) {
      console.error("Error deleting media:", error);
      // Don't show error toast for delete failures as it's not critical
      return false;
    }
  };

  const createPreviewUrl = (file) => {
    return URL.createObjectURL(file);
  };

  const cleanupPreviewUrl = (url) => {
    if (url && url.startsWith("blob:")) {
      URL.revokeObjectURL(url);
    }
  };

  const handleMediaUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file
    if (!file.type.startsWith("image/") && !file.type.startsWith("video/")) {
      toast.error(t("Please select a valid image or video file"));
      return;
    }

    // Create preview URL
    const preview = createPreviewUrl(file);

    // Set preview for inline display
    setSelectedFile(file);
    setPreviewUrl(preview);
    setIsMediaUploaded(false);
    setUploadedMediaData(null);

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    // Start upload automatically
    const uploadResult = await uploadMedia(file);
    if (uploadResult) {
      setUploadedMediaData(uploadResult);
      setIsMediaUploaded(true);
    } else {
      // Upload failed, clear preview
      handleClearPreview();
    }
  };

  const handleSendMedia = async () => {
    if (!selectedFile || !isMediaUploaded || !uploadedMediaData) return;

    // Send media message with already uploaded data
    await handleSendMediaMessage(uploadedMediaData, mediaCaption.trim());

    // Cleanup - clear both media preview and text input
    handleClearPreview();

    // Also clear the regular message input to ensure no text remains
    setNewMessage("");
  };

  const handleClearPreview = () => {
    cleanupPreviewUrl(previewUrl);
    setSelectedFile(null);
    setPreviewUrl("");
    setMediaCaption("");
    setUploadedMediaData(null);
    setIsMediaUploaded(false);

    // Also ensure the main message input is cleared
    setNewMessage("");
  };

  const handleSendMediaMessage = async (mediaData, caption = "") => {
    if (!ticket?.threadUuid || !isConnected) {
      console.error("Cannot send media message:", {
        threadUuid: ticket?.threadUuid,
        isConnected,
      });
      if (!isConnected) {
        toast.error(
          t(
            "WebSocket connection is not established. Please wait and try again.",
          ),
        );
      }
      // Delete uploaded media if message can't be sent
      await deleteMedia(mediaData.filename);
      return;
    }

    setSending(true);

    // Determine sender type based on company ID
    const senderType = companyId !== 1 ? "CLIENT" : "SUPPORT";

    // Determine if it's image or video
    const isVideo =
      mediaData.fileType && mediaData.fileType.startsWith("video/");

    // Determine message type based on content
    let messageType;
    if (caption && caption.trim()) {
      messageType = "MIXED"; // Both media and caption
    } else {
      messageType = isVideo ? "VIDEO" : "IMAGE"; // Media only
    }

    // Create optimistic message for immediate UI update
    const optimisticMessage = {
      id: `optimistic_${Date.now()}`,
      threadUuid: ticket.threadUuid,
      senderId: parseInt(userId, 10),
      senderType: senderType,
      message: caption || "",
      messageType: messageType,
      attachmentUrl: mediaData.filename,
      createdAt: new Date().toISOString(),
      senderDetails: null,
      isOptimistic: true,
      timestamp: Date.now(),
      mediaData: mediaData, // Store for cleanup on error
      fileType: mediaData.fileType,
      hasCaption: !!(caption && caption.trim()),
    };

    try {
      // Immediately add message to chat for better UX
      setChatMessages((prev) => [...prev, optimisticMessage]);

      // Set timeout to convert optimistic message to real message if no WebSocket response
      const timeoutId = setTimeout(() => {
        setChatMessages((prev) => {
          return prev.map((msg) => {
            if (msg.id === optimisticMessage.id && msg.isOptimistic) {
              console.warn(
                "Converting optimistic image message to real message due to timeout:",
                msg,
              );
              return {
                ...msg,
                id: `real_${Date.now()}`,
                isOptimistic: false,
              };
            }
            return msg;
          });
        });
        optimisticMessageTimeoutRef.current.delete(optimisticMessage.id);
      }, 5000);

      optimisticMessageTimeoutRef.current.set(optimisticMessage.id, timeoutId);

      // Scroll to bottom immediately
      setTimeout(() => {
        if (chatContainerRef.current) {
          chatContainerRef.current.scrollTop =
            chatContainerRef.current.scrollHeight;
        }
      }, 50);

      // Send via WebSocket with separate attachmentUrl
      const messageToSend = caption || "";

      try {
        await sendWebSocketMessage(
          messageToSend,
          senderType,
          mediaData.filename,
          messageType,
        );
      } catch (wsError) {
        console.error("WebSocket sending failed:", wsError);
        throw wsError; // Re-throw to be caught by outer try-catch
      }

      // Stop typing indicator
      sendTypingIndicator(false, companyId !== 1 ? "Client" : "Support Agent");
    } catch (error) {
      console.error("Error sending image message:", error);

      // Remove optimistic message on error
      setChatMessages((prev) => {
        const timeoutId = optimisticMessageTimeoutRef.current.get(
          optimisticMessage.id,
        );
        if (timeoutId) {
          clearTimeout(timeoutId);
          optimisticMessageTimeoutRef.current.delete(optimisticMessage.id);
        }
        return prev.filter((msg) => msg.id !== optimisticMessage.id);
      });

      // Delete uploaded media on error
      await deleteMedia(mediaData.filename);

      if (error.message.includes("Sender not found")) {
        toast.error(
          t(
            "User authentication error. Please refresh the page and try again.",
          ),
        );
      } else {
        const mediaType = isVideo ? "video" : "image";
        toast.error(t(`Failed to send ${mediaType}: `) + error.message);
      }
    } finally {
      setSending(false);
    }
  };

  const handleMediaPreview = (mediaUrl, fileType) => {
    setPreviewMediaUrl(mediaUrl);
    setPreviewMediaType(fileType || "image");
    setShowMediaPreview(true);
  };

  const handleImageHover = (e, show) => {
    if (show && previewUrl) {
      const rect = e.target.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let x = rect.right + 30; // Position to the right by default
      let y = rect.top - 100;

      // If there's not enough space on the right, position to the left
      if (x + 500 > viewportWidth) {
        x = rect.left - 500 - 30;
      }

      // Ensure it doesn't go above viewport
      if (y < 30) {
        y = 30;
      }

      // Ensure it doesn't go below viewport
      if (y + 500 > viewportHeight) {
        y = viewportHeight - 500 - 30;
      }

      setImagePreviewPosition({ x, y });
      setShowImagePreview(true);
    } else {
      setShowImagePreview(false);
    }
  };

  // Helper function to ensure proper image URL construction
  const getMediaUrl = (url) => {
    if (!url) return "";
    const fullUrl = config.AWS_PUBLIC_BUCKET_URL + url;
    return fullUrl;
  };

  if (loading || !ticket) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "400px" }}
      >
        <Loading size="xl" variant="dots" />
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      <style>{`
        .typing-indicator {
          display: inline-flex;
          align-items: center;
          gap: 4px;
        }
        .typing-indicator span {
          width: 8px;
          height: 8px;
          background-color: #999;
          border-radius: 50%;
          display: inline-block;
          animation: typing 1.4s infinite;
        }
        .typing-indicator span:nth-child(2) {
          animation-delay: 0.2s;
        }
        .typing-indicator span:nth-child(3) {
          animation-delay: 0.4s;
        }
        @keyframes typing {
          0%, 60%, 100% {
            transform: translateY(0);
          }
          30% {
            transform: translateY(-10px);
          }
        }
      `}</style>
      <div className="content container-fluid">
        <Breadcrumbs
          maintitle={
            typeof ticket?.subject === "string"
              ? ticket.subject
              : "Support Ticket"
          }
          title="Dashboard"
          subtitle="Support Ticket"
        />

        <div className="row">
          {/* Main Chat Content */}
          <div className="col-md-8">
            <Card className="h-100">
              <Card.Header className="d-flex justify-content-between align-items-center border-bottom">
                <div>
                  <h5 className="mb-0 text-primary">
                    {companyId === 1
                      ? `Chat with ${ticket?.raisedByUser?.fullName || "Raised By User"}`
                      : ticket?.assignedToSupport
                        ? "Chat with Agent " +
                          (ticket?.assignedToSupport?.fullName ||
                            ticket?.assignedToSupport)
                        : "Awaiting Assignment"}
                  </h5>
                </div>
                <div className="d-flex align-items-center">
                  <span
                    className={`badge ${isConnected ? "bg-success" : "bg-danger"} me-2`}
                    style={{ fontSize: "10px" }}
                  >
                    {isConnected ? "● Connected" : "● Disconnected"}
                  </span>
                </div>
              </Card.Header>

              <Card.Body className="p-0">
                {/* Chat Section */}
                <div
                  className="chat-section h-100 d-flex flex-column"
                  style={{ height: "calc(100vh - 200px)" }}
                >
                  {/* Chat Messages */}
                  <div
                    ref={chatContainerRef}
                    className="chat-messages flex-grow-1 p-4"
                    style={{
                      height: "calc(100vh - 350px)",
                      overflowY: "auto",
                    }}
                  >
                    {chatMessages && chatMessages.length > 0 ? (
                      chatMessages.map((message) => { 
                        const isAdminUser = companyId === 1;  
                        const isSupportMessage = message.senderType === "SUPPORT";
                        const isOwnSenderId = message.senderId == userId;
                        const isOwnMessage = isOwnSenderId || (isAdminUser && isSupportMessage);
                        return (
                          <div
                            key={
                              message.id ||
                              `${message.message}-${message.createdAt}`
                            }
                            className={`message mb-4 ${isOwnMessage ? "text-end" : "text-start"}`}
                          >
                            <div
                              className={`message-bubble p-3 ${
                                isOwnMessage
                                  ? "bg-primary text-white ms-auto"
                                  : "bg-white text-dark"
                              } ${message.isOptimistic ? "opacity-75" : ""}`}
                              style={{
                                maxWidth: "75%",
                                display: "inline-block",
                                borderRadius: isOwnMessage
                                  ? "20px 20px 5px 20px"
                                  : "20px 20px 20px 5px",
                                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                                border: !isOwnMessage
                                  ? "1px solid #e3e6ea"
                                  : "none",
                                fontSize: "14px",
                                lineHeight: "1.4",
                                position: "relative",
                              }}
                            >
                              <div className="message-content">
                                {message.attachmentUrl ? (
                                  <div>
                                    {/* Media Display */}
                                    {message.messageType === "VIDEO" ||
                                    (message.fileType &&
                                      message.fileType.startsWith("video/")) ? (
                                      <video
                                        controls
                                        style={{
                                          maxWidth: "100%",
                                          maxHeight: "200px",
                                          borderRadius: "10px",
                                          display: "block",
                                          marginBottom: message.hasCaption
                                            ? "8px"
                                            : "0",
                                        }}
                                        onClick={() =>
                                          handleMediaPreview(
                                            getMediaUrl(message.attachmentUrl),
                                            "video",
                                          )
                                        }
                                      >
                                        <source
                                          src={getMediaUrl(
                                            message.attachmentUrl,
                                          )}
                                        />
                                        Your browser does not support the video
                                        tag.
                                      </video>
                                    ) : (
                                      <img
                                        src={getMediaUrl(message.attachmentUrl)}
                                        alt="Attachment"
                                        style={{
                                          maxWidth: "100%",
                                          maxHeight: "200px",
                                          borderRadius: "10px",
                                          cursor: "pointer",
                                          display: "block",
                                          marginBottom: message.hasCaption
                                            ? "8px"
                                            : "0",
                                        }}
                                        onClick={() =>
                                          handleMediaPreview(
                                            getMediaUrl(message.attachmentUrl),
                                            "image",
                                          )
                                        }
                                        onLoad={(e) => {
                                          // Hide fallback text when image loads successfully
                                          const parent = e.target.parentElement;
                                          if (parent) {
                                            const fallback =
                                              parent.querySelector(
                                                'div[data-fallback="true"]',
                                              );
                                            if (fallback)
                                              fallback.style.display = "none";
                                          }
                                        }}
                                        onError={(e) => {
                                          console.error(
                                            "Image failed to load:",
                                            getMediaUrl(message.attachmentUrl),
                                          );
                                          e.target.style.display = "none";
                                          const parent = e.target.parentElement;
                                          if (parent) {
                                            const fallback =
                                              parent.querySelector(
                                                'div[data-fallback="true"]',
                                              );
                                            if (fallback)
                                              fallback.style.display = "block";
                                          }
                                        }}
                                      />
                                    )}
                                    {/* Caption Display */}
                                    {(message.hasCaption ||
                                      message.messageType === "MIXED") &&
                                      message.message &&
                                      message.message.trim() && (
                                        <div
                                          style={{
                                            fontSize: "14px",
                                            lineHeight: "1.4",
                                            color: isOwnMessage
                                              ? "#fff"
                                              : "#333",
                                          }}
                                        >
                                          {message.message}
                                        </div>
                                      )}
                                    {/* Error Fallback */}
                                    <div
                                      data-fallback="true"
                                      style={{
                                        display: "none",
                                        color: isOwnMessage ? "#fff" : "#666",
                                        fontSize: "14px",
                                        textAlign: "center",
                                        padding: "20px",
                                        border: `1px dashed ${isOwnMessage ? "rgba(255,255,255,0.3)" : "#ddd"}`,
                                        borderRadius: "10px",
                                      }}
                                    >
                                      <i
                                        className="fa fa-image mb-2"
                                        style={{
                                          fontSize: "24px",
                                          opacity: 0.5,
                                        }}
                                      ></i>
                                      <br />
                                      📷 Media (failed to load)
                                    </div>
                                  </div>
                                ) : (
                                  message.message
                                )}
                              </div>
                              <div
                                className="message-time mt-1"
                                style={{ fontSize: "11px", opacity: 0.7 }}
                              >
                                {new Date(
                                  dayjs
                                    .utc(message.timestamp || message.createdAt)
                                    .local()
                                    .format(),
                                ).toLocaleString()}
                                {message.isOptimistic && (
                                  <span className="ms-1">⏳</span>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="text-center text-muted py-4">
                        <p>No messages yet. Start the conversation!</p>
                      </div>
                    )}

                    {/* Typing Indicator */}
                    {isTyping && (
                      <div className="message mb-3 text-start">
                        <div
                          className="message-bubble p-3"
                          style={{
                            backgroundColor: "#f1f1f1",
                            color: "#666",
                            maxWidth: "200px",
                            display: "inline-block",
                            borderRadius: "18px",
                            fontSize: "14px",
                            lineHeight: "1.4",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                          >
                            <div
                              className="typing-dot"
                              style={{
                                width: "6px",
                                height: "6px",
                                backgroundColor: "#666",
                                borderRadius: "50%",
                                animation:
                                  "typing-dot 1.4s infinite ease-in-out both",
                                animationDelay: "0s",
                              }}
                            ></div>
                            <div
                              className="typing-dot"
                              style={{
                                width: "6px",
                                height: "6px",
                                backgroundColor: "#666",
                                borderRadius: "50%",
                                animation:
                                  "typing-dot 1.4s infinite ease-in-out both",
                                animationDelay: "0.2s",
                              }}
                            ></div>
                            <div
                              className="typing-dot"
                              style={{
                                width: "6px",
                                height: "6px",
                                backgroundColor: "#666",
                                borderRadius: "50%",
                                animation:
                                  "typing-dot 1.4s infinite ease-in-out both",
                                animationDelay: "0.4s",
                              }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Message Input */}
                  <div
                    className="message-input p-3 border-top bg-white"
                    style={{ minHeight: "80px" }}
                  >
                    {/* Media Preview Thumbnail */}
                    {selectedFile && previewUrl && (
                      <div
                        className="mb-3 p-3"
                        style={{
                          backgroundColor: "#ffffff",
                          borderRadius: "12px",
                          border: "1px solid #e3e6ea",
                          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                        }}
                      >
                        <div className="d-flex align-items-center gap-3">
                          {/* Enhanced Thumbnail */}
                          <div
                            className="position-relative"
                            style={{
                              borderRadius: "6px",
                              overflow: "hidden",
                              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.12)",
                              cursor: "pointer",
                              transition: "transform 0.2s ease",
                            }}
                          >
                            {selectedFile.type.startsWith("video/") ? (
                              <>
                                <video
                                  src={previewUrl}
                                  style={{
                                    width: "40px",
                                    height: "40px",
                                    objectFit: "cover",
                                    display: "block",
                                  }}
                                  onMouseEnter={(e) => {
                                    e.target.style.transform = "scale(1.1)";
                                    handleImageHover(e, true);
                                  }}
                                  onMouseLeave={(e) => {
                                    e.target.style.transform = "scale(1)";
                                    handleImageHover(e, false);
                                  }}
                                  muted
                                />
                                {/* Video Play Icon Overlay */}
                                <div
                                  style={{
                                    position: "absolute",
                                    top: "50%",
                                    left: "50%",
                                    transform: "translate(-50%, -50%)",
                                    backgroundColor: "rgba(0, 0, 0, 0.7)",
                                    borderRadius: "50%",
                                    width: "18px",
                                    height: "18px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    pointerEvents: "none",
                                  }}
                                >
                                  <i
                                    className="fa fa-play"
                                    style={{
                                      color: "white",
                                      fontSize: "8px",
                                      marginLeft: "1px",
                                    }}
                                  ></i>
                                </div>
                              </>
                            ) : (
                              <img
                                src={previewUrl}
                                alt="Preview"
                                style={{
                                  width: "40px",
                                  height: "40px",
                                  objectFit: "cover",
                                  display: "block",
                                  transition: "transform 0.2s ease",
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.transform = "scale(1.1)";
                                  handleImageHover(e, true);
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.transform = "scale(1)";
                                  handleImageHover(e, false);
                                }}
                              />
                            )}
                            {/* Hover Indicator */}
                            <div
                              style={{
                                position: "absolute",
                                bottom: "2px",
                                right: "2px",
                                backgroundColor: "rgba(255, 255, 255, 0.95)",
                                borderRadius: "3px",
                                padding: "1px 3px",
                                fontSize: "8px",
                                color: "#666",
                                fontWeight: "600",
                              }}
                            >
                              <i
                                className="fa fa-search-plus"
                                style={{ fontSize: "7px" }}
                              ></i>
                            </div>
                          </div>

                          {/* File Info */}
                          <div className="flex-grow-1" style={{ minWidth: 0 }}>
                            <div className="d-flex align-items-center gap-2 mb-1">
                              <i
                                className={`fa ${selectedFile.type.startsWith("video/") ? "fa-video" : "fa-image"}`}
                                style={{ color: "#6c757d", fontSize: "14px" }}
                              ></i>
                              <span
                                style={{
                                  fontWeight: "500",
                                  color: "#333",
                                  fontSize: "14px",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {selectedFile.name}
                              </span>
                            </div>
                            <div className="d-flex align-items-center gap-3">
                              <small
                                style={{ color: "#6c757d", fontWeight: "500" }}
                              >
                                {(selectedFile.size / 1024 / 1024).toFixed(2)}{" "}
                                MB
                              </small>
                              <small
                                style={{
                                  color: isMediaUploaded
                                    ? "#28a745"
                                    : uploadingMedia
                                      ? "#ffc107"
                                      : "#6c757d",
                                  backgroundColor: isMediaUploaded
                                    ? "rgba(40, 167, 69, 0.1)"
                                    : uploadingMedia
                                      ? "rgba(255, 193, 7, 0.1)"
                                      : "rgba(108, 117, 125, 0.1)",
                                  padding: "2px 8px",
                                  borderRadius: "12px",
                                  fontSize: "11px",
                                  fontWeight: "500",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "4px",
                                }}
                              >
                                {uploadingMedia ? (
                                  <>
                                    <i
                                      className="fa fa-spinner fa-spin"
                                      style={{ fontSize: "10px" }}
                                    ></i>{" "}
                                    Uploading...
                                  </>
                                ) : isMediaUploaded ? (
                                  <>
                                    <i
                                      className="fa fa-check"
                                      style={{ fontSize: "10px" }}
                                    ></i>{" "}
                                    Ready to send
                                  </>
                                ) : (
                                  <>
                                    <i
                                      className="fa fa-clock"
                                      style={{ fontSize: "10px" }}
                                    ></i>{" "}
                                    Processing...
                                  </>
                                )}
                              </small>
                            </div>
                          </div>

                          {/* Remove Button */}
                          <button
                            type="button"
                            className="btn btn-outline-danger"
                            onClick={handleClearPreview}
                            style={{
                              width: "32px",
                              height: "32px",
                              padding: "0",
                              fontSize: "12px",
                              borderRadius: "50%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              transition: "all 0.2s ease",
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.transform = "scale(1.1)";
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.transform = "scale(1)";
                            }}
                          >
                            <i className="fa fa-times"></i>
                          </button>
                        </div>
                      </div>
                    )}

                    <div className="d-flex gap-2 align-items-end">
                      <div className="flex-grow-1">
                        <Form.Control
                          as="textarea"
                          rows={selectedFile ? 2 : 1}
                          placeholder={
                            ticket?.status === "CLOSED"
                              ? "This ticket is closed. No messages can be sent."
                              : selectedFile
                              ? "Add a caption (optional)..."
                              : "Type your message here..."
                          }
                          value={selectedFile ? mediaCaption : newMessage}
                          onChange={(e) => {
                            if (selectedFile) {
                              setMediaCaption(e.target.value);
                            } else {
                              handleTyping(e.target.value);
                            }
                          }}
                          disabled={ticket?.status === "CLOSED"}
                          onKeyPress={(e) => {
                            if (e.key === "Enter" && !e.shiftKey && ticket?.status !== "CLOSED") {
                              e.preventDefault();
                              if (selectedFile) {
                                handleSendMedia();
                              } else {
                                handleSendMessage();
                              }
                            }
                          }}
                          style={{
                            resize: "none",
                            minHeight: "48px",
                            borderRadius: "24px",
                            paddingLeft: "20px",
                            paddingRight: "20px",
                            paddingTop: "12px",
                            paddingBottom: "12px",
                            border: "2px solid #e3e6ea",
                            fontSize: "14px",
                            lineHeight: "1.4",
                            transition: "all 0.2s ease",
                            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
                          }}
                          onFocus={(e) => {
                            e.target.style.borderColor = "#007bff";
                            e.target.style.boxShadow =
                              "0 4px 12px rgba(0, 123, 255, 0.15)";
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = "#e3e6ea";
                            e.target.style.boxShadow =
                              "0 2px 8px rgba(0, 0, 0, 0.05)";
                          }}
                        />
                      </div>
                      <div className="d-flex gap-2">
                        <input
                          type="file"
                          ref={fileInputRef}
                          style={{ display: "none" }}
                          accept="image/*,video/*"
                          onChange={handleMediaUpload}
                        />
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          style={{
                            borderRadius: "50%",
                            width: "40px",
                            height: "40px",
                          }}
                          onClick={() => fileInputRef.current?.click()}
                          disabled={uploadingMedia || sending || selectedFile || ticket?.status === "CLOSED"}
                          title={
                            ticket?.status === "CLOSED"
                              ? "Ticket is closed"
                              : selectedFile
                              ? "File selected"
                              : "Upload Image or Video"
                          }
                        >
                          {uploadingMedia ? (
                            <i className="fa fa-spinner fa-spin" />
                          ) : selectedFile ? (
                            <i
                              className="fa fa-check"
                              style={{ color: "#28a745" }}
                            />
                          ) : (
                            <i className="fa fa-paperclip" />
                          )}
                        </Button>
                        <Button
                          variant="primary"
                          onClick={
                            selectedFile ? handleSendMedia : handleSendMessage
                          }
                          disabled={
                            ticket?.status === "CLOSED" ||
                            sending ||
                            (!selectedFile && !newMessage.trim()) ||
                            (selectedFile &&
                              (!isMediaUploaded || uploadingMedia))
                          }
                          style={{
                            borderRadius: "25px",
                            paddingLeft: "20px",
                            paddingRight: "20px",
                            opacity:
                              selectedFile &&
                              (!isMediaUploaded || uploadingMedia)
                                ? 0.6
                                : 1,
                            transition: "all 0.2s ease",
                          }}
                        >
                          {ticket?.status === "CLOSED"
                            ? t("Ticket Closed")
                            : t("Send")}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </div>

          {/* Right Side Panel */}
          <div className="col-md-4">
            {/* Client Details - Only show for company ID 1 (support agents) */}
            {companyId === 1 && (
              <Card className="mb-2">
                <Card.Body className="pb-1">
                  <h5 className="mb-0 text-muted mb-2">Raised By Details</h5>
                  <div className="client-info">
                    <div className="mb-2">
                      <strong>Name:</strong>{" "}
                      {typeof ticket?.raisedByUser?.fullName === "string"
                        ? ticket.raisedByUser.fullName
                        : "N/A"}
                    </div>
                    <div className="mb-2">
                      <strong>Email:</strong>{" "}
                      {typeof ticket?.raisedByUser?.officialEmail === "string"
                        ? ticket.raisedByUser.officialEmail
                        : "N/A"}
                    </div>
                    <div className="mb-2">
                      <strong>Phone:</strong>{" "}
                      {typeof ticket?.raisedByUser?.number === "string"
                        ? ticket.raisedByUser.number
                        : "N/A"}
                    </div>
                  </div>
                </Card.Body>
              </Card>
            )}

            {/* Ticket Details */}
            <Card>
              <Card.Body>
                <h5 className="mb-0 text-muted mb-2">Ticket Details</h5>
                <div className="ticket-info">
                  <div className="mb-2">
                    <strong>Ticket Code:</strong>{" "}
                    {typeof ticket?.ticketCode === "string"
                      ? ticket.ticketCode
                      : "N/A"}
                  </div>
                  <div className="mb-2">
                    <strong>Subject:</strong>{" "}
                    {typeof ticket?.subject === "string"
                      ? ticket.subject
                      : "N/A"}
                  </div>
                  <div className="mb-2">
                    <strong>Category:</strong>{" "}
                    {typeof ticket?.category === "string"
                      ? ticket.category
                      : "N/A"}
                  </div>
                  <div className="mb-2">
                    <strong>Status:</strong>{" "}
                    <SupportTicketStatusBadge
                      status={
                        typeof ticket?.status === "string"
                          ? ticket.status
                          : "OPEN"
                      }
                      type="status"
                    />
                  </div>
                  <div className="mb-2">
                    <strong>Priority:</strong>{" "}
                    <SupportTicketStatusBadge
                      status={
                        typeof ticket?.priority === "string"
                          ? ticket.priority
                          : "MEDIUM"
                      }
                      type="priority"
                    />
                  </div>
                  <div className="mb-0">
                    <div className="text-muted small mt-3">
                      <strong>Created:</strong>
                      <br />
                      {ticket?.createdAt && typeof ticket.createdAt === "string"
                        ? new Date(
                            dayjs.utc(ticket.createdAt).local().format(),
                          ).toLocaleString()
                        : "N/A"}
                    </div>
                  </div>
                  
                  {/* Close Ticket Button - Only for support team */}
                  {companyId === 1 && ticket?.status !== "CLOSED" && (
                    <div className="mt-3 pt-3 border-top">
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={handleCloseTicket}
                        disabled={closingTicket}
                        className="w-100"
                      >
                        {closingTicket ? (
                          <>
                            <i className="fa fa-spinner fa-spin me-2" />
                            {t("Closing Ticket...")}
                          </>
                        ) : (
                          <>
                            <i className="fa fa-times me-2" />
                            {t("Close Ticket")}
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                  
                  {/* Closed Ticket Notice */}
                  {ticket?.status === "CLOSED" && (
                    <div className="mt-3 pt-3 border-top">
                      <div className="alert alert-info mb-0" style={{ fontSize: "14px" }}>
                        <i className="fa fa-info-circle me-2" />
                        {t("This ticket is closed. No new messages can be sent.")}
                      </div>
                    </div>
                  )}
                </div>
              </Card.Body>
            </Card>
          </div>
        </div>
      </div>

      {/* Enhanced Hover Image Preview */}
      {showImagePreview && previewUrl && (
        <div
          style={{
            position: "fixed",
            left: imagePreviewPosition.x,
            top: imagePreviewPosition.y,
            zIndex: 10000,
            backgroundColor: "rgba(0, 0, 0, 0.95)",
            borderRadius: "16px",
            padding: "16px",
            minWidth: "450px",
            minHeight: "300px",
            maxWidth: "600px",
            maxHeight: "600px",
            pointerEvents: "none",
            boxShadow: "0 12px 40px rgba(0, 0, 0, 0.6)",
            border: "2px solid rgba(255, 255, 255, 0.15)",
            animation:
              "fadeInScale 0.25s cubic-bezier(0.4, 0, 0.2, 1) forwards",
            backdropFilter: "blur(8px)",
          }}
        >
          <style>
            {`
              @keyframes fadeInScale {
                from {
                  opacity: 0;
                  transform: scale(0.85) translateY(10px);
                }
                to {
                  opacity: 1;
                  transform: scale(1) translateY(0);
                }
              }
            `}
          </style>

          {selectedFile?.type?.startsWith("video/") ? (
            <>
              <video
                src={previewUrl}
                style={{
                  width: "100%",
                  height: "auto",
                  maxWidth: "568px",
                  maxHeight: "568px",
                  borderRadius: "12px",
                  objectFit: "contain",
                  minHeight: "300px",
                }}
                muted
                autoPlay
                loop
              />
              <div
                style={{
                  position: "absolute",
                  bottom: "20px",
                  left: "20px",
                  backgroundColor: "rgba(255, 255, 255, 0.95)",
                  padding: "8px 12px",
                  borderRadius: "8px",
                  fontSize: "13px",
                  fontWeight: "600",
                  color: "#333",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <i className="fa fa-video" style={{ color: "#dc3545" }}></i>
                Video Preview
              </div>
            </>
          ) : (
            <>
              <img
                src={previewUrl}
                alt="Preview"
                style={{
                  width: "100%",
                  height: "auto",
                  maxWidth: "568px",
                  maxHeight: "568px",
                  borderRadius: "12px",
                  objectFit: "contain",
                  minHeight: "300px",
                }}
              />
              <div
                style={{
                  position: "absolute",
                  bottom: "20px",
                  left: "20px",
                  backgroundColor: "rgba(255, 255, 255, 0.95)",
                  padding: "8px 12px",
                  borderRadius: "8px",
                  fontSize: "13px",
                  fontWeight: "600",
                  color: "#333",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <i className="fa fa-image" style={{ color: "#28a745" }}></i>
                Image Preview
              </div>
            </>
          )}

          {/* Preview Info */}
          <div
            style={{
              position: "absolute",
              top: "16px",
              right: "16px",
              backgroundColor: "rgba(255, 255, 255, 0.95)",
              padding: "6px 10px",
              borderRadius: "8px",
              fontSize: "12px",
              fontWeight: "600",
              color: "#666",
              display: "flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            <i className="fa fa-file" style={{ fontSize: "10px" }}></i>
            {(selectedFile?.size / 1024 / 1024).toFixed(2)} MB
          </div>

          {/* Zoom Indicator */}
          <div
            style={{
              position: "absolute",
              top: "16px",
              left: "16px",
              backgroundColor: "rgba(0, 123, 255, 0.9)",
              padding: "6px 10px",
              borderRadius: "8px",
              fontSize: "12px",
              fontWeight: "600",
              color: "white",
              display: "flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            <i className="fa fa-search-plus" style={{ fontSize: "10px" }}></i>
            Zoomed View
          </div>
        </div>
      )}

      {/* Media Preview Modal */}
      <Modal
        show={showMediaPreview}
        onHide={() => setShowMediaPreview(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {previewMediaType === "video" ? "Video" : "Image"} Preview
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center p-0">
          {previewMediaType === "video" ? (
            <video
              controls
              src={previewMediaUrl}
              style={{
                maxWidth: "100%",
                maxHeight: "70vh",
                objectFit: "contain",
              }}
            />
          ) : (
            <img
              src={previewMediaUrl}
              alt="Preview"
              style={{
                maxWidth: "100%",
                maxHeight: "70vh",
                objectFit: "contain",
              }}
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowMediaPreview(false)}
          >
            Close
          </Button>
          <Button
            variant="primary"
            onClick={() => window.open(previewMediaUrl, "_blank")}
          >
            Open in New Tab
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Add CSS for animations */}
      <style jsx>{`
        @keyframes typing-dot {
          0%,
          60%,
          100% {
            transform: translateY(0);
            opacity: 0.4;
          }
          30% {
            transform: translateY(-10px);
            opacity: 1;
          }
        }

        .typing-indicator span {
          display: inline-block;
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background-color: #999;
          margin: 0 1px;
          animation: typing-dot 1.4s infinite ease-in-out both;
        }

        .typing-indicator span:nth-child(1) {
          animation-delay: 0s;
        }
        .typing-indicator span:nth-child(2) {
          animation-delay: 0.2s;
        }
        .typing-indicator span:nth-child(3) {
          animation-delay: 0.4s;
        }
      `}</style>
    </div>
  );
};

export default SupportTicketDetail;
